import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Eye, Bookmark, MessageCircle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RatingStars } from '@/components/community/RatingStars';
import { useResourceFavorites } from '@/hooks/useFavorites';
import { useToast } from '@/hooks/use-toast';
import { Resource } from '@/hooks/useResources';
import { useCommentCount } from '@/hooks/community/useCommentCount';
import { cn } from '@/lib/utils';

interface ResourceCardProps {
  resource: Resource;
  aiExplanation?: {
    reason: string;
    relevanceScore: number;
  };
}

export function ResourceCard({ resource, aiExplanation }: ResourceCardProps) {
  const { resourceFavorites, toggleResourceFavorite } = useResourceFavorites();
  const { toast } = useToast();
  
  // Create deterministic UUID from resource name for community features
  const createResourceId = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Convert to UUID format using the hash
    const hex = (Math.abs(hash).toString(16) + '000000000').substring(0, 8);
    return `${hex.substring(0, 8)}-0000-4000-8000-${hex}${hex.substring(0, 4)}`;
  };
  
  const resourceId = createResourceId(resource["Resource Name"]);
  const { count: commentCount } = useCommentCount('resource', resourceId);
  const isFavorite = resourceFavorites.includes(resource["Resource Name"]);

  const handleExternalLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (resource["Link"]) {
      window.open(resource["Link"], '_blank', 'noopener,noreferrer');
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleResourceFavorite(resource["Resource Name"]);
    toast({
      title: isFavorite ? "Removed from favorites" : "Added to favorites",
      description: isFavorite 
        ? `${resource["Resource Name"]} removed from your favorites`
        : `${resource["Resource Name"]} added to your favorites`,
    });
  };

  // Parse topic tags and use cases
  const topics = resource["Topic Tags"] 
    ? resource["Topic Tags"].split(',').map(t => t.trim()).filter(Boolean)
    : [];

  const useCases = resource["Recommended Use Case"]
    ? resource["Recommended Use Case"].split(/[,•\n]/).map(c => c.trim()).filter(Boolean)
    : [];

  return (
    <Card className="group hover:shadow-lg hover:shadow-accent/10 transition-all duration-300 hover:scale-[1.02] h-full flex flex-col border-border/50 hover:border-accent/30">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-lg text-text-primary line-clamp-2 group-hover:text-primary transition-colors duration-200">
              {resource["Resource Name"]}
            </h3>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="secondary" className="text-xs">
                {resource["Content Type"]}
              </Badge>
              {resource["Last Updated"] && (
                <span className="text-xs text-text-tertiary">
                  Updated {resource["Last Updated"]}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        {resource["Description"] && (
          <p className="text-sm text-text-secondary line-clamp-3 mb-4 flex-1">
            {resource["Description"]}
          </p>
        )}

        {/* Topics */}
        {topics.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {topics.slice(0, 3).map((topic, index) => (
                 <Badge 
                   key={index} 
                   variant="outline" 
                   className="text-xs px-2 py-1 truncate"
                 >
                   {topic}
                 </Badge>
              ))}
              {topics.length > 3 && (
                <Badge variant="outline" className="text-xs px-2 py-1 text-text-tertiary">
                  +{topics.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Use Cases */}
        {useCases.length > 0 && (
          <div className="mb-4">
            <span className="text-xs text-text-tertiary mb-1 block">Use Cases:</span>
            <div className="flex flex-wrap gap-1">
              {useCases.slice(0, 2).map((useCase, index) => (
                 <Badge 
                   key={index} 
                   variant="accent" 
                   className="text-xs px-2 py-1 truncate"
                 >
                   {useCase}
                 </Badge>
              ))}
              {useCases.length > 2 && (
                <Badge variant="accent" className="text-xs px-2 py-1 text-text-tertiary">
                  +{useCases.length - 2}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Community Features */}
        <div className="mb-3 flex items-center justify-between">
          <RatingStars entityType="resource" entityId={resourceId} compact={true} />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFavoriteClick}
              className={cn(
                "h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-all duration-base",
                isFavorite && "opacity-100 text-primary"
              )}
            >
              <Bookmark className={cn("h-3 w-3", isFavorite && "fill-current")} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-all duration-base relative"
            >
              <MessageCircle className="h-3 w-3" />
              {commentCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                  {commentCount > 99 ? '99+' : commentCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* AI Explanation */}
        {aiExplanation && (
          <div className="mb-4 p-3 bg-gradient-to-r from-accent-light/10 to-accent-light/5 border border-accent/20 rounded-lg animate-scale-in">
            <div className="flex items-start space-x-2">
              <Sparkles className="h-4 w-4 text-accent flex-shrink-0 mt-0.5 animate-pulse" />
              <div className="flex-1">
                <p className="text-sm text-text-secondary leading-relaxed">
                  {aiExplanation.reason}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="outline" className="text-xs bg-accent-light/20 text-accent border-accent/30">
                    {aiExplanation.relevanceScore}% Match
                  </Badge>
                  <span className="text-xs text-text-tertiary">AI Recommended</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-auto pt-4">
          <Button 
            asChild
            variant="outline" 
            size="sm" 
            className="flex-1"
          >
            <Link to={`/resources/${encodeURIComponent(resource["Resource Name"])}`}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </Link>
          </Button>
          {resource["Link"] && (
            <Button 
              onClick={handleExternalLink}
              variant="outline"
              size="sm" 
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}